import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppLayout from '../../../component/appLayout';
import { BottomData, Table } from '../styles/styles';
import {
  Header,
  ContentHeading,
  RightContent,
  LeftContent,
  Content,
  DateDropBox,
  DropdownButton,
  HeaderLeft,
  TableSection,
  HeaderRight,
  HeaderTitle,
  BoxWrapper
} from '../styles';
import { useHistory } from 'react-router-dom';
import { ProjectDetailInterface, ProjectExportDataInterface } from '../../../interfaces/ProjectInterface';
import { getCustomerList } from '../../../services/customerServices';
import { CustomerDetailsInterface } from '../../../interfaces/CustomerInterface';
import { currencyWithDecimal, isEmpty } from '../../../helpers/common';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { SelectionRangeInterface } from '../../../interfaces/TimeReportInterface';
import {
  BUTTON_TYPE,
  CHIP_ICON_POSITION,
  CHIP_SIZE,
  CHIP_TYPE,
  EMPTY_STATE_SIZE,
  REPORT_PAYMENT_LOADING_STATE_DATA,
  TIME_DATE_SELECTED_TEXT
} from '../../../global/constants';
import { getPaymentReportData } from '../../../services/reportService';
import { PaymentReportDetails } from '../../../interfaces/ReportInterface';
import { CSVLink } from 'react-csv';
import { PaymentReportCSVHeader } from '../../../global/row';
import { captureException } from '../../../services/logService';
import ReportDurationDropdown from '../../../component/dropdowns/reportDurationDropdown';
import ReportLoadingState from '../../../component/loading/reportLoadingState';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import { rootStore } from '../../../mobx/rootStore';
import { ProjectModel } from '../../../mobx/models/project';
import { observer } from 'mobx-react-lite';
import { useMobileDevice } from '../../../global/useMobile';
import ResponsiveHeader from '../../../component/responsiveHeader';
import BottomSubNavigation from '../../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import Chip from '../../../component/chipComponent';
import Button from '../../../component/button';
import EmptyStateV2 from '../../../component/emptyState';
import dayjs from 'dayjs';
import { ROUTES } from "../../../routes/urls";

const Payments: React.FC = () => {
  // Refs
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const outsideRef = useRef<HTMLDivElement | null>(null);

  // States
  const [dateSelectedText, setDateSelectedText] = useState('Custom');
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [selectionRange, setSelectionRange] = useState<SelectionRangeInterface>({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection'
  });
  const [exportData, setExportData] = useState<ProjectExportDataInterface[]>([]);
  const [projectList, setProjectList] = useState<ProjectDetailInterface[]>([]);
  const [customerList, setCustomerList] = useState<CustomerDetailsInterface[]>([]);
  const [filteredCustomerList, setFilteredCustomerList] = useState<CustomerDetailsInterface[]>([]);
  const [stateLoading, setStateLoading] = useState<boolean>(false);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [selectAllCustomer, setSelectAllCustomer] = useState(true);

  //mobx variables
  const { currentUser, workspaceStore, reportStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { paymentReportData, filteredPaymentReportData, loading: reducerLoading } = reportStore;

  // Other variables
  const history = useHistory();
  let loading = stateLoading || reducerLoading;
  const isMobile = useMobileDevice();

  // Add useEffect for outside click handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;

      if (
        outsideRef.current &&
        !outsideRef.current.contains(targetElement) &&
        !targetElement.closest('.calendar-dropdown')
      ) {
        setIsDateDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const applyFilter = useCallback(
    (data: PaymentReportDetails[], projects?: ProjectDetailInterface[], customers?: CustomerDetailsInterface[]) => {
      let filteredData = JSON.parse(JSON.stringify(data || []));
      const filteredCustomers: string[] = customers?.filter((item) => item?.checked)?.map((x) => x?.id) || [];
      filteredData = filteredData.filter((item: PaymentReportDetails) => filteredCustomers.includes(item?.customerId));
      reportStore.setFilteredPaymentReportData(filteredData);
      setIsCustomerDropdownOpen(false);
      if (filteredData?.length > 0) {
        const data = filteredData?.map((item: PaymentReportDetails) => {
          return {
            invoiceNumber: item?.invoiceNumber,
            projects: !isEmpty(item?.project) ? item?.project?.map((project) => project?.name).join(', ') : '',
            customerCompanyName: item?.customerCompanyName,
            paidOn: dayjs(item?.paidOn).format(currentUser?.dateFormat),
            paidAmount: currencyWithDecimal(item?.paidAmount)
          };
        });
        setExportData(data);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportStore, currentUser?.dateFormat]
  );

  const loadData = useCallback(async () => {
    try {
      setStateLoading(true);
      if (workspace?.id && !isEmpty(workspace?.id)) {
        if (!workspace?.isAdmin) history.push(ROUTES.INBOX);
        const result = await Promise.all([
          getPaymentReportData(workspace?.id, selectionRange?.startDate as Date, selectionRange?.endDate as Date),
          getCustomerList()
        ]);
        const projects = rootStore.projectStore.projects || [];
        const projectsResponse: ProjectDetailInterface[] = projects?.map((item: ProjectModel) => {
          return { ...item, checked: true };
        }) as unknown as ProjectDetailInterface[];
        let customerResponse = result[1];
        customerResponse = customerResponse?.map((item: CustomerDetailsInterface) => {
          return { ...item, checked: true };
        });
        applyFilter(result[0], projectsResponse, customerResponse);
        setProjectList(projectsResponse);

        setCustomerList(customerResponse);
        setFilteredCustomerList(customerResponse);
      }
    } catch (e) {
      captureException(e);
    } finally {
      setStateLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionRange.endDate, workspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData, history]);

  useEffect(() => {
    return () => {
      reportStore.setFilteredPaymentReportData([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openMemberDropdown = useCallback((value: boolean) => {
    setIsDateDropdownOpen(false);
    setIsStatusDropdownOpen(false);
    setIsCustomerDropdownOpen(value);
  }, []);

  const onSelectAllCustomerClick = useCallback(
    (customerList: CustomerDetailsInterface[], value: boolean, projects: ProjectDetailInterface[]) => {
      setSelectAllCustomer(value);
      let customerListClone: CustomerDetailsInterface[] = JSON.parse(JSON.stringify(customerList || []));
      customerListClone = customerListClone?.map((item) => {
        return { ...item, checked: value };
      });
      setCustomerList(customerListClone);
      setFilteredCustomerList(customerListClone);
      applyFilter(paymentReportData, projects, customerListClone);
      setIsCustomerDropdownOpen(false);
    },
    [applyFilter, paymentReportData]
  );

  const onChangeSearchCustomer = useCallback(
    (value: string) => {
      setSearchCustomer(value);
      let timeout;
      if (!isEmpty(value)) {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          const newList =
            customerList?.filter((item: CustomerDetailsInterface) =>
              item?.companyName?.toLowerCase().includes(value?.toLowerCase())
            ) || [];
          setFilteredCustomerList(newList);
        }, 300);
      } else {
        setFilteredCustomerList(customerList);
      }
    },
    [customerList]
  );

  const onCheckCustomer = useCallback(
    (
      allUserList: CustomerDetailsInterface[],
      filteredList: CustomerDetailsInterface[],
      userId: string,
      value: boolean,
      projects: ProjectDetailInterface[]
    ) => {
      const updatedAllList = allUserList?.map((item) => {
        return item?.id === userId ? { ...item, checked: value } : item;
      }, []);
      const updatedFilteredList = filteredList?.map((item) => {
        return item?.id === userId ? { ...item, checked: value } : item;
      }, []);
      const uncheckedTasks = updatedFilteredList?.find((x) => !x?.checked);
      if (uncheckedTasks || updatedFilteredList?.length === 0) setSelectAllCustomer(false);
      else setSelectAllCustomer(true);
      setCustomerList(updatedAllList);
      setFilteredCustomerList(updatedFilteredList);
      applyFilter(paymentReportData, projects, updatedAllList);
    },
    [applyFilter, paymentReportData]
  );

  const renderCustomerData = useMemo(() => {
    return (
      <BottomData>
        {isEmpty(searchCustomer) && (
          <DropdownItem
            label='Select All'
            isCheckBox={true}
            isChecked={selectAllCustomer}
            onClick={() => onSelectAllCustomerClick(filteredCustomerList, !selectAllCustomer, projectList)}
          />
        )}
        {(isEmpty(searchCustomer) ? customerList : filteredCustomerList)?.map((item) => {
          return (
            <DropdownItem
              key={item?.id}
              label={item?.companyName}
              isCheckBox={true}
              isChecked={item?.checked}
              onClick={(e) =>
                onCheckCustomer(customerList, filteredCustomerList, item?.id, !item?.checked, projectList)
              }
            />
          );
        })}
      </BottomData>
    );
  }, [
    searchCustomer,
    selectAllCustomer,
    customerList,
    filteredCustomerList,
    onSelectAllCustomerClick,
    projectList,
    onCheckCustomer
  ]);

  const openStatusDropdown = useCallback((value: boolean) => {
    setIsStatusDropdownOpen(value);
    setIsDateDropdownOpen(false);
    setIsCustomerDropdownOpen(false);
  }, []);

  const openDateDropdown = useCallback((value: boolean) => {
    setIsDateDropdownOpen(value);
    setIsCustomerDropdownOpen(false);
  }, []);

  const handleSelect = useCallback((ranges: { selection: { startDate: Date; endDate: Date; key: string } }) => {
    setSelectionRange(ranges.selection);
    setDateSelectedText(TIME_DATE_SELECTED_TEXT.CUSTOM);
  }, []);

  const renderCsvOption = () => {
    return (
      filteredPaymentReportData?.length > 0 && (
        <CSVLink data={exportData || []} headers={PaymentReportCSVHeader} filename={`payment_report.csv`}>
          {!isMobile ? (
            <Button
              title='Export as CSV'
              iconName='download-icon'
              variant={BUTTON_TYPE.SECONDARY}
              size='small'
              iconPosition='start'
            />
          ) : (
            <BottomNavigationItem iconName='download-icon' />
          )}
        </CSVLink>
      )
    );
  };

  const renderFilterOptions = useMemo(() => {
    return (
      <ContentHeading>
        <LeftContent>
          {/* client dropdown */}
          <Dropdown
            modalTitle='Client'
            onChangeSearch={onChangeSearchCustomer}
            content={
              <Chip
                size={CHIP_SIZE.SMALL}
                variant={CHIP_TYPE.FILL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                title='Client'
                trailingIconName='down-arrow-icon'
                trailingIconClassName={isCustomerDropdownOpen ? 'dropdown-icon ' : ''}
                onClick={() => openMemberDropdown(!isCustomerDropdownOpen)}
                isActive={isCustomerDropdownOpen}
              />
            }
            trigger='click'
            className='third-child'
            onOutsideClick={() => setIsCustomerDropdownOpen(false)}>
            {renderCustomerData}
          </Dropdown>
        </LeftContent>
        {/* right content */}
        <RightContent>
          {/* custom date dropdown */}
          <Dropdown
            modalTitle='Custom Date'
            isMinWidth={152}
            content={
              <Chip
                title={dateSelectedText}
                variant={CHIP_TYPE.FILL}
                size={CHIP_SIZE.SMALL}
                iconColorType='primary'
                iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
                trailingIconName='down-arrow-icon'
                className='custom-dropdown'
                trailingIconClassName={isStatusDropdownOpen ? 'dropdown-icon ' : ''}
                onClick={() => openStatusDropdown(!isStatusDropdownOpen)}
                isActive={isStatusDropdownOpen}
              />
            }
            trigger='click'
            onOutsideClick={() => setIsStatusDropdownOpen(false)}>
            <ReportDurationDropdown
              buttonRef={buttonRef}
              setSelectionRange={(value) => setSelectionRange(value)}
              setIsStatusDropdownOpen={(value) => setIsStatusDropdownOpen(value as boolean)}
              setDateSelectedText={(value) => setDateSelectedText(value)}
            />
          </Dropdown>
          {/* calendar dropdown */}
          <DropdownButton isMonthPicker>
            <Chip
              variant={CHIP_TYPE.FILL}
              size={CHIP_SIZE.SMALL}
              iconColorType='primary'
              iconPosition={CHIP_ICON_POSITION.TEXT_RIGHT_BUTTON}
              title={`${dayjs(selectionRange?.startDate).format('MMM DD')} - ${dayjs(selectionRange?.endDate).format(
                'MMM DD'
              )}`}
              leadingIconName='calendar-icon'
              trailingIconName='down-arrow-icon'
              className='calendar-dropdown'
              trailingIconClassName={isDateDropdownOpen ? 'dropdown-icon ' : ''}
              onClick={() => openDateDropdown(!isDateDropdownOpen)}
              isActive={isDateDropdownOpen}
            />
            {isDateDropdownOpen && (
              <DateDropBox ref={outsideRef}>
                <DateRange
                  editableDateInputs={true}
                  onChange={handleSelect}
                  moveRangeOnFirstSelection={false}
                  ranges={[selectionRange]}
                  maxDate={new Date()}
                />
              </DateDropBox>
            )}
          </DropdownButton>
        </RightContent>
      </ContentHeading>
    );
  }, [
    onChangeSearchCustomer,
    isCustomerDropdownOpen,
    renderCustomerData,
    dateSelectedText,
    isStatusDropdownOpen,
    selectionRange,
    isDateDropdownOpen,
    handleSelect,
    openMemberDropdown,
    openStatusDropdown,
    openDateDropdown
  ]);

  return (
    <>
      <AppLayout>
        <BoxWrapper>
          {!isMobile ? (
            <Header>
              <HeaderLeft>
                <HeaderTitle>Payment</HeaderTitle>
              </HeaderLeft>
              <HeaderRight>
                {!isMobile && renderFilterOptions}
                {renderCsvOption()}
              </HeaderRight>
            </Header>
          ) : (
            <ResponsiveHeader title='Payment' noMargin={true} />
          )}
          <Content>
            {isMobile && renderFilterOptions}
            {(filteredPaymentReportData?.length === 0 || !filteredPaymentReportData) && !loading && (
              <EmptyStateV2
                size={EMPTY_STATE_SIZE.MEDIUM}
                title={'There is no report data.'}
                description={'You have no report data for this date. Search for another date.'}
                imageUrl={'reports_empty'}
                isAlignedCenter={true}
              />
            )}
            {loading && <ReportLoadingState header={REPORT_PAYMENT_LOADING_STATE_DATA} />}
            {!loading && filteredPaymentReportData?.length > 0 && (
              <TableSection>
                <Table isPayment>
                  <thead>
                    <tr>
                      <th>invoice no</th>
                      <th>Projects</th>
                      <th>Client Company</th>
                      <th>paid on</th>
                      <th>paid amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPaymentReportData?.map((item: PaymentReportDetails) => (
                      <tr key={item['_id']}>
                        <td>{item?.invoiceNumber}</td>
                        <td>{item?.project?.map((project) => project.name).join(', ')}</td>
                        <td>{item?.customerCompanyName}</td>
                        <td>{dayjs(item?.paidOn).format(currentUser?.dateFormat)}</td>
                        <td>
                          {currencyWithDecimal(item?.paidAmount)} {item?.invoiceCurrency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableSection>
            )}
          </Content>
        </BoxWrapper>
      </AppLayout>
      <BottomSubNavigation rightElement={renderCsvOption()} />
    </>
  );
};

export default observer(Payments);
